import { loadTranslations } from '@angular/localize';
import { getLocaleDirection, registerLocaleData } from '@angular/common';
import localeEn from '@angular/common/locales/en';
import localeFr from '@angular/common/locales/fr';
import localeEs from '@angular/common/locales/es';
import localeAr from '@angular/common/locales/ar';
import localeEt from '@angular/common/locales/et';
import localePt from '@angular/common/locales/pt';
import { environment } from '../environments/environment';
import { Token } from './models/token';
import { FormioToken } from './models/formio-token';
import { RESTHeartTranslationResponse } from './models/restheart-translation-response';
import {
  LOCAL_STORAGE_KEYS,
  SESSION_STORAGE_KEYS
} from './constants/storage-keys';
import { generateRandomString, removeQueryParams } from './utils/string-utils';
import { getLanguageWithFallback } from './utils/storage-utils';
import { isSameOriginUrl, routerUrlToAbsolute } from './utils/url-utils';

export async function initializeLanguage(): Promise<void> {
  let language: string | null = null;

  // Safely access localStorage (may throw in incognito mode or with security restrictions)
  try {
    language = localStorage.getItem(LOCAL_STORAGE_KEYS.LANGUAGE);
  } catch (e) {
    console.warn('Failed to read language from localStorage:', e);
  }

  if (!language) {
    // Get default, validating it's not a placeholder
    const envDefault = environment.DEFAULT_LANGUAGE;
    language =
      envDefault &&
      !envDefault.toUpperCase().includes('PLACEHOLDER') &&
      !envDefault.toUpperCase().includes('DEFAULT_LANGUAGE')
        ? envDefault
        : 'en';

    // Try to persist, but don't fail if we can't
    try {
      localStorage.setItem(LOCAL_STORAGE_KEYS.LANGUAGE, language);
    } catch (e) {
      console.warn('Failed to persist language to localStorage:', e);
      // Continue anyway - we have a valid language value in memory
    }
  }

  if (language) {
    // Add cache-busting timestamp to ensure fresh translations are loaded
    const cacheBuster = Date.now();
    const generalTranslationsPromise = fetch(
      `${environment.RESTHEART_URL}documents/translation/general/?_t=${cacheBuster}`,
      { cache: 'no-store' }
    )
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        return response.json();
      })
      .then((response: RESTHeartTranslationResponse) => {
        if (!response?.translations) {
          return {};
        }
        for (const [key, value] of Object.entries(response.translations)) {
          if (key === language) {
            return value;
          }
        }
        return {};
      })
      .catch(() => {
        return {};
      });

    // Wait for general translations to load
    const generalTranslationsResult = await Promise.allSettled([
      generalTranslationsPromise
    ]);

    // Use only general translations (service translations now loaded on-demand)
    const translations =
      generalTranslationsResult[0]?.status === 'fulfilled'
        ? generalTranslationsResult[0].value
        : {};

    // Ensure all translation values are strings
    const sanitizedTranslations = sanitizeTranslations(translations);

    // Set global GLOBAL_TRANSLATIONS for Formio forms
    // This is required by Formio's synchronous translation lookup
    window.GLOBAL_TRANSLATIONS = sanitizedTranslations;

    loadTranslations(sanitizedTranslations);
  }
}

/**
 * Ensures all translation values are strings
 * @param translations The translations object to sanitize
 * @returns A new object with all values converted to strings
 */
function sanitizeTranslations(
  translations: Record<string, any>
): Record<string, string> {
  const result: Record<string, string> = {};

  if (!translations || typeof translations !== 'object') {
    console.warn(
      'Invalid translations object provided to sanitizeTranslations'
    );
    return result;
  }

  for (const [key, value] of Object.entries(translations)) {
    try {
      const sanitizedValue = sanitizeTranslationValue(key, value);
      if (sanitizedValue !== null) {
        // eslint-disable-next-line security/detect-object-injection -- key from Object.entries() iteration
        result[key] = sanitizedValue;
      }
    } catch {
      // Skip this translation entry
    }
  }

  return result;
}

/**
 * Converts a single translation value to string format
 * @param key The translation key (for logging)
 * @param value The value to sanitize
 * @returns The sanitized string value or null if should be skipped
 */
function sanitizeTranslationValue(key: string, value: any): string | null {
  if (value === null || value === undefined) {
    console.warn(`Skipping null or undefined translation for key: ${key}`);
    return null;
  }

  if (typeof value === 'string') {
    return value;
  }

  if (typeof value === 'object') {
    return sanitizeObjectValue(key, value);
  }

  // For other types (number, boolean, etc.), convert to string
  return String(value);
}

/**
 * Converts an object or array value to string format
 * @param key The translation key (for logging)
 * @param value The object or array to sanitize
 * @returns The sanitized string value
 */
function sanitizeObjectValue(key: string, value: any): string {
  if (Array.isArray(value)) {
    return sanitizeArrayValue(key, value);
  }

  if (Object.keys(value).length > 0) {
    return sanitizeNestedObject(key, value);
  }

  // Empty object, convert to string
  return JSON.stringify(value);
}

/**
 * Converts an array value to a comma-separated string
 * @param key The translation key (for logging)
 * @param value The array to sanitize
 * @returns The sanitized string value
 */
function sanitizeArrayValue(key: string, value: any[]): string {
  console.warn(`Converting array to string for translation key: ${key}`);
  const stringArray = value.map((item) =>
    typeof item === 'object' ? JSON.stringify(item) : String(item)
  );
  return stringArray.join(', ');
}

/**
 * Recursively sanitizes a nested object
 * @param key The translation key (for logging)
 * @param value The nested object to sanitize
 * @returns The sanitized string value
 */
function sanitizeNestedObject(key: string, value: Record<string, any>): string {
  console.warn(
    `Converting nested object to string for translation key: ${key}`
  );
  const nestedObj = sanitizeTranslations(value);

  if (Object.keys(nestedObj).length > 0) {
    return JSON.stringify(nestedObj);
  }

  // Otherwise use a simple string representation
  return String(value);
}

/**
 * Builds a CAS redirect URI using base URL + path only (no query/hash)
 */
function buildCasRedirectUri(pathOnly?: string): string {
  try {
    const base = (environment.OAUTH_REDIRECT_URL ?? '').replace(/\/+$/, '');
    let path = pathOnly ?? (window.location.pathname || '/');
    if (!path.startsWith('/')) {
      path = '/' + path;
    }
    return base ? `${base}${path}` : environment.OAUTH_REDIRECT_URL;
  } catch (e) {
    return environment.OAUTH_REDIRECT_URL;
  }
}

/**
 * Parses authorization code from URL query parameters
 */
function parseAuthorizationCode(): string | null {
  try {
    return new URL(window.location.href).searchParams.get('code');
  } catch (e) {
    return null;
  }
}

/**
 * Gets current path only (no query string or hash)
 */
function getCurrentPathOnly(): string | undefined {
  try {
    const url = new URL(window.location.href);
    return url.pathname || undefined;
  } catch (e) {
    const href = window.location.href || '';
    return href.split('?')[0].split('#')[0] || undefined;
  }
}

/**
 * Saves the current full URL for redirect after login
 */
function savePostLoginRedirect(): void {
  try {
    const fullTarget = `${window.location.pathname || '/'}${window.location.search || ''}${window.location.hash || ''}`;
    sessionStorage.setItem(
      SESSION_STORAGE_KEYS.POST_LOGIN_REDIRECT,
      fullTarget
    );
  } catch (e) {
    // ignore storage errors
  }
}

/**
 * Generates and stores a new CAS auth state
 */
function generateAndStoreCasState(): string {
  const state = generateRandomString();
  try {
    sessionStorage.setItem(SESSION_STORAGE_KEYS.CAS_AUTH_STATE, state);
  } catch (e) {
    // ignore storage errors
  }
  return state;
}

/**
 * Builds the CAS login URL with all required parameters
 */
function buildCasLoginUrl(redirectUri: string, state: string): string {
  const lang = getLanguageWithFallback();
  return `${environment.CAS_URL}cas/spa.html#/?response_type=code&client_id=${encodeURIComponent(environment.OAUTH_CLIENT_ID)}&state=${encodeURIComponent(state)}&scope=any&redirect_uri=${encodeURIComponent(redirectUri)}&lang=${encodeURIComponent(lang)}`;
}

/**
 * Initiates redirect to CAS login page
 */
function redirectToCasLogin(): void {
  savePostLoginRedirect();
  const pathOnly = getCurrentPathOnly();
  const state = generateAndStoreCasState();
  const redirectUri = buildCasRedirectUri(pathOnly);
  const loginUrl = buildCasLoginUrl(redirectUri, state);
  window.location.href = loginUrl;
}

/**
 * Checks if CAS login should be initiated
 */
function shouldInitiateCasLogin(
  code: string | null,
  existingJwt: string | null
): boolean {
  return !code && !existingJwt;
}

/**
 * Parses state parameter from URL
 */
function parseStateFromUrl(): string | null {
  try {
    return new URL(window.location.href).searchParams.get('state');
  } catch (e) {
    return null;
  }
}

/**
 * Gets stored CAS state from session storage
 */
function getStoredCasState(): string | null {
  try {
    return sessionStorage.getItem(SESSION_STORAGE_KEYS.CAS_AUTH_STATE);
  } catch (e) {
    return null;
  }
}

/**
 * Validates CAS state parameter against stored value
 */
function validateCasState(): { isValid: boolean; jwtExists: boolean } {
  const returnedState = parseStateFromUrl();
  const expectedState = getStoredCasState();
  const jwtExists = !!localStorage.getItem(Token.tokenNameInStorage);

  const isValid =
    !!returnedState && !!expectedState && returnedState === expectedState;

  return { isValid, jwtExists };
}

/**
 * Cleans code and state parameters from URL
 */
function cleanUrlParams(): void {
  try {
    const currentUrl = window.location.href;
    const cleanUrl = removeQueryParams(currentUrl);
    if (cleanUrl !== currentUrl) {
      window.history.replaceState({}, document.title, cleanUrl);
    }
  } catch (e) {
    // ignore
  }
}

/**
 * Exchanges authorization code for access tokens
 */
async function exchangeCodeForTokens(
  code: string,
  redirectUri: string
): Promise<{ access: string; refresh: string } | null> {
  const form = new URLSearchParams();
  form.set('code', code);
  form.set('redirect_uri', redirectUri);
  form.set('grant_type', 'authorization_code');

  const resp = await fetch(`${environment.CAS_URL}access_token`, {
    method: 'POST',
    headers: {
      Authorization:
        'Basic ' +
        btoa(`${environment.OAUTH_CLIENT_ID}:${environment.OAUTH_SECRET}`),
      'Content-Type': 'application/x-www-form-urlencoded'
    },
    body: form.toString()
  });

  if (!resp.ok) {
    return null;
  }

  const data: any = await resp.json();
  const access = data?.access_token;
  const refresh = data?.refresh_token;

  return access && refresh ? { access, refresh } : null;
}

/**
 * Stores authentication tokens in localStorage
 */
function storeAuthTokens(access: string, refresh: string): void {
  try {
    localStorage.setItem(Token.tokenNameInStorage, String(access));
    localStorage.setItem(
      FormioToken.tokenNameInStorage,
      FormioToken.scheme + String(access)
    );
    localStorage.setItem(Token.refreshTokenNameInStorage, String(refresh));
  } catch (e) {
    // ignore storage errors
  }
}

/**
 * Clears CAS auth state from session storage
 */
function clearCasAuthState(): void {
  try {
    sessionStorage.removeItem(SESSION_STORAGE_KEYS.CAS_AUTH_STATE);
  } catch (e) {
    // ignore
  }
}

/**
 * Determines the URL to redirect to after successful login
 */
function determinePostLoginUrl(): string {
  try {
    const saved = sessionStorage.getItem(
      SESSION_STORAGE_KEYS.POST_LOGIN_REDIRECT
    );
    if (saved) {
      sessionStorage.removeItem(SESSION_STORAGE_KEYS.POST_LOGIN_REDIRECT);
      if (saved.startsWith('http')) {
        if (isSameOriginUrl(saved)) {
          return saved;
        }
        return removeQueryParams(window.location.href);
      }
      return routerUrlToAbsolute(saved);
    }
  } catch (e) {
    // ignore storage errors
  }

  return removeQueryParams(window.location.href);
}

/**
 * Redirects to the final URL after login
 */
function redirectToFinalUrl(finalUrl: string): void {
  const currentUrl = window.location.href;
  if (finalUrl && currentUrl !== finalUrl) {
    window.history.replaceState({}, document.title, finalUrl);
  }
}

/**
 * Gets path from saved post-login redirect
 */
function getPathFromSavedRedirect(): string | undefined {
  try {
    const saved = sessionStorage.getItem(
      SESSION_STORAGE_KEYS.POST_LOGIN_REDIRECT
    );
    if (saved) {
      const u = new URL(saved, window.location.origin);
      return u.pathname || undefined;
    }
  } catch (e) {
    // ignore
  }
  return undefined;
}

/**
 * Handles the complete authorization code exchange flow
 */
async function handleCodeExchange(code: string): Promise<void> {
  const pathOnly = getPathFromSavedRedirect();
  const redirectUri = buildCasRedirectUri(pathOnly);

  const tokens = await exchangeCodeForTokens(code, redirectUri);
  if (!tokens) {
    return;
  }

  storeAuthTokens(tokens.access, tokens.refresh);
  clearCasAuthState();

  const finalUrl = determinePostLoginUrl();
  redirectToFinalUrl(finalUrl);
}

export function initializeCasAuthEarly(): () => Promise<void> {
  return async () => {
    try {
      if (environment.KEYCLOAK_ACTIVE) {
        return;
      }

      const code = parseAuthorizationCode();
      const existingJwt = localStorage.getItem(Token.tokenNameInStorage);

      if (shouldInitiateCasLogin(code, existingJwt)) {
        redirectToCasLogin();
        return;
      }

      if (!code) {
        return;
      }

      const stateValidation = validateCasState();
      if (!stateValidation.isValid) {
        if (!stateValidation.jwtExists) {
          redirectToCasLogin();
        } else {
          cleanUrlParams();
        }
        return;
      }

      await handleCodeExchange(code);
    } catch (e) {
      // swallow errors
    }
  };
}

export const initializeSupportedLocales = (): string => {
  // Register all supported locales
  registerLocaleData(localeEt, 'et');
  registerLocaleData(localeFr, 'fr');
  registerLocaleData(localeEn, 'en');
  registerLocaleData(localeEs, 'es');
  registerLocaleData(localeAr, 'ar');
  registerLocaleData(localePt, 'pt');

  const supportedLocales = ['et', 'fr', 'en', 'es', 'ar', 'pt'];
  const hardcodedDefault = 'en';
  let language: string | null = null;

  // Safely access localStorage (may throw in incognito mode or with security restrictions)
  try {
    language = localStorage.getItem(LOCAL_STORAGE_KEYS.LANGUAGE);
  } catch (e) {
    console.warn(
      'Failed to access localStorage for language, using default:',
      e
    );
  }

  // Validate environment default (check for placeholder strings)
  const envDefault = environment.DEFAULT_LANGUAGE;
  const isValidEnvDefault =
    envDefault &&
    supportedLocales.includes(envDefault) &&
    !envDefault.toUpperCase().includes('PLACEHOLDER') &&
    !envDefault.toUpperCase().includes('DEFAULT_LANGUAGE');

  // Determine final locale with fallback chain: localStorage → environment → hardcoded default
  let finalLocale = hardcodedDefault;

  if (language && supportedLocales.includes(language)) {
    finalLocale = language;
  } else if (isValidEnvDefault) {
    finalLocale = envDefault!;
  }

  // Set document attributes
  try {
    document.documentElement.lang = finalLocale;
    document.documentElement.dir = getLocaleDirection(finalLocale);
  } catch (e) {
    console.warn('Failed to set document language attributes:', e);
  }

  return finalLocale;
};
